import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { projectsAPI, clientsAPI, servicesAPI, timeEntriesAPI, usersAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, formatCurrency, getStatusColor, getStatusLabel, getServiceTypeLabel, isOverdue, isWithinDays } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RichTextEditor } from '../components/RichTextEditor';
import { QuickClientDialog } from '../components/QuickClientDialog';
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  CalendarIcon,
  Clock,
  Users,
  Play,
  Pause,
  CheckCircle,
  Eye,
  LayoutGrid,
  List,
  Paperclip,
  FileText,
  Download,
  StickyNote,
  Send,
  AlertTriangle,
  ListTodo,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';

import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const projectSchema = z.object({
  nome: z.string().min(2, 'Nome richiesto'),
  client_id: z.string().min(1, 'Cliente richiesto'),
  servizi: z.array(z.string()).optional(),
  descrizione: z.string().optional(),
  descrizione_html: z.string().optional(),
  budget: z.coerce.number().min(0).default(0),
  data_inizio: z.date().optional(),
  data_fine_prevista: z.date().optional(),
  team_members: z.array(z.string()).optional(),
});

const timeEntrySchema = z.object({
  user_id: z.string().min(1, 'Seleziona un membro del team'),
  ore: z.coerce.number().min(0.5, 'Minimo 0.5 ore'),
  data: z.string().min(1, 'Data richiesta'),
  descrizione: z.string().optional(),
  costo_orario: z.coerce.number().min(0).optional(),
});

const FILE_SIZE_LIMIT = 10 * 1024 * 1024; // 10 MB

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const Progetti = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [myProjectsOnly, setMyProjectsOnly] = useState(false);
  const [sortBy, setSortBy] = useState('recente');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [timeDialogOpen, setTimeDialogOpen] = useState(false);
  const [editingTimeEntry, setEditingTimeEntry] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [taskEditOpen, setTaskEditOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskEditForm, setTaskEditForm] = useState({ titolo: '', descrizione: '', assegnato_a: '', data_scadenza: '', priorita: '' });
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      servizi: [],
      team_members: [],
      budget: 0,
      descrizione_html: '',
    },
  });

  const {
    register: registerTime,
    handleSubmit: handleSubmitTime,
    reset: resetTime,
    control: controlTime,
    formState: { errors: timeErrors },
  } = useForm({
    resolver: zodResolver(timeEntrySchema),
    defaultValues: {
      user_id: '',
      ore: 1,
      data: new Date().toISOString().split('T')[0],
      costo_orario: 50,
    },
  });

  const selectedServices = watch('servizi') || [];
  const selectedTeam = watch('team_members') || [];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, clientsRes, servicesRes, usersRes] = await Promise.all([
        projectsAPI.getAll(),
        clientsAPI.getAll(),
        servicesAPI.getAll(),
        usersAPI.getAll(),
      ]);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
      setServices(servicesRes.data);
      setUsers(usersRes.data);
      const projectId = searchParams.get('project');
      if (projectId) {
        const target = projectsRes.data.find(p => p.id === projectId);
        if (target) {
          setSelectedProject(target);
          setDetailDialogOpen(true);
          setSearchParams({}, { replace: true });
        }
      }
    } catch (error) {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeEntries = async (projectId) => {
    try {
      const response = await timeEntriesAPI.getAll(projectId);
      setTimeEntries(response.data);
    } catch (error) {
      console.error('Error fetching time entries:', error);
    }
  };

  const fetchAttachments = async (projectId) => {
    try {
      const res = await projectsAPI.getAttachments(projectId);
      setAttachments(res.data);
    } catch (e) {
      console.error('Error fetching attachments:', e);
    }
  };

  const fetchNotes = async (projectId) => {
    try {
      const res = await projectsAPI.getNotes(projectId);
      setNotes(res.data);
    } catch (e) {
      console.error('Error fetching notes:', e);
    }
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        data_inizio: data.data_inizio?.toISOString(),
        data_fine_prevista: data.data_fine_prevista?.toISOString(),
      };
      if (editingProject) {
        await projectsAPI.update(editingProject.id, payload);
        toast.success('Progetto aggiornato');
      } else {
        await projectsAPI.create(payload);
        toast.success('Progetto creato');
      }
      fetchData();
      setDialogOpen(false);
      setEditingProject(null);
      reset({ nome: '', client_id: '', servizi: [], team_members: [], budget: 0, descrizione_html: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const onSubmitTime = async (data) => {
    try {
      const payload = {
        project_id: selectedProject.id,
        user_id: data.user_id,
        ore: data.ore,
        data: data.data,
        descrizione: data.descrizione,
        costo_orario: data.costo_orario,
      };
      if (editingTimeEntry) {
        await timeEntriesAPI.update(editingTimeEntry.id, payload);
        toast.success('Registrazione aggiornata');
      } else {
        await timeEntriesAPI.create(payload);
        toast.success('Ore registrate');
      }
      fetchData();
      fetchTimeEntries(selectedProject.id);
      setTimeDialogOpen(false);
      setEditingTimeEntry(null);
      resetTime({ user_id: '', ore: 1, data: new Date().toISOString().split('T')[0], costo_orario: 50 });
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleEditTimeEntry = (entry) => {
    setEditingTimeEntry(entry);
    const dateStr = entry.data ? entry.data.split('T')[0] : new Date().toISOString().split('T')[0];
    resetTime({
      user_id: entry.user_id,
      ore: entry.ore,
      data: dateStr,
      descrizione: entry.descrizione || '',
      costo_orario: entry.costo_orario || 50,
    });
    setTimeDialogOpen(true);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    reset({
      nome: project.nome || '',
      client_id: project.client_id || '',
      descrizione: project.descrizione || '',
      descrizione_html: project.descrizione_html || '',
      budget: project.budget ?? 0,
      servizi: project.servizi || [],
      team_members: project.team_members || [],
      data_inizio: project.data_inizio ? new Date(project.data_inizio) : undefined,
      data_fine_prevista: project.data_fine_prevista ? new Date(project.data_fine_prevista) : undefined,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo progetto?')) return;
    try {
      await projectsAPI.delete(id);
      toast.success('Progetto eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await projectsAPI.updateStatus(id, status);
      toast.success('Stato aggiornato');
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const fetchTasks = async (projectId) => {
    try {
      const res = await projectsAPI.getTasks(projectId);
      setTasks(res.data);
    } catch (e) {
      console.error('Error fetching tasks:', e);
    }
  };

  const handleViewDetail = (project) => {
    setSelectedProject(project);
    fetchTimeEntries(project.id);
    fetchAttachments(project.id);
    fetchNotes(project.id);
    fetchTasks(project.id);
    setDetailDialogOpen(true);
  };

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return;
    try {
      await projectsAPI.createTask(selectedProject.id, { titolo: newTaskTitle.trim(), sort_order: tasks.length });
      setNewTaskTitle('');
      fetchTasks(selectedProject.id);
    } catch {
      toast.error('Errore aggiunta attività');
    }
  };

  const handleOpenEditTask = (task) => {
    setEditingTask(task);
    setTaskEditForm({
      titolo: task.titolo || '',
      descrizione: task.descrizione || '',
      assegnato_a: task.assegnato_a || 'none',
      data_scadenza: task.data_scadenza ? task.data_scadenza.split('T')[0] : '',
      priorita: task.priorita || 'none',
    });
    setTaskEditOpen(true);
  };

  const handleSaveEditTask = async () => {
    if (!editingTask || !taskEditForm.titolo.trim()) return;
    try {
      await projectsAPI.updateTask(selectedProject.id, editingTask.id, {
        titolo: taskEditForm.titolo.trim(),
        descrizione: taskEditForm.descrizione || null,
        assegnato_a: taskEditForm.assegnato_a === 'none' ? null : taskEditForm.assegnato_a || null,
        data_scadenza: taskEditForm.data_scadenza ? new Date(taskEditForm.data_scadenza).toISOString() : null,
        priorita: taskEditForm.priorita === 'none' ? null : taskEditForm.priorita || null,
      });
      fetchTasks(selectedProject.id);
      setTaskEditOpen(false);
      setEditingTask(null);
      toast.success('Attività aggiornata');
    } catch {
      toast.error('Errore salvataggio attività');
    }
  };

  const handleMoveTask = async (task, direction, idx) => {
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= tasks.length) return;
    const newOrder = [...tasks];
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
    try {
      await Promise.all(newOrder.map((t, i) => projectsAPI.updateTask(selectedProject.id, t.id, { sort_order: i })));
      fetchTasks(selectedProject.id);
    } catch {
      toast.error('Errore riordino attività');
    }
  };

  const handleToggleTask = async (task) => {
    try {
      await projectsAPI.updateTask(selectedProject.id, task.id, { completata: !task.completata });
      fetchTasks(selectedProject.id);
    } catch {
      toast.error('Errore aggiornamento attività');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await projectsAPI.deleteTask(selectedProject.id, taskId);
      fetchTasks(selectedProject.id);
    } catch {
      toast.error('Errore eliminazione attività');
    }
  };

  const handleDeleteTimeEntry = async (id) => {
    try {
      await timeEntriesAPI.delete(id);
      toast.success('Registrazione eliminata');
      fetchData();
      fetchTimeEntries(selectedProject.id);
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > FILE_SIZE_LIMIT) {
      toast.error('File troppo grande (max 10 MB)');
      return;
    }
    setUploadingFile(true);
    try {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        const base64 = evt.target.result;
        await projectsAPI.addAttachment(selectedProject.id, {
          nome: file.name,
          file_data: base64,
          file_type: file.type,
          file_size: file.size,
        });
        toast.success('Allegato caricato');
        fetchAttachments(selectedProject.id);
        setUploadingFile(false);
      };
      reader.onerror = () => { toast.error('Errore lettura file'); setUploadingFile(false); };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error('Errore upload');
      setUploadingFile(false);
    }
    e.target.value = '';
  };

  const handleDeleteAttachment = async (attachId) => {
    try {
      await projectsAPI.deleteAttachment(selectedProject.id, attachId);
      toast.success('Allegato eliminato');
      fetchAttachments(selectedProject.id);
    } catch {
      toast.error('Errore eliminazione allegato');
    }
  };

  const handleDownloadAttachment = async (attach) => {
    try {
      const res = await projectsAPI.downloadAttachment(selectedProject.id, attach.id);
      const { file_data, nome, file_type } = res.data;
      const link = document.createElement('a');
      link.href = file_data;
      link.download = nome;
      link.click();
    } catch {
      toast.error('Errore download');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await projectsAPI.addNote(selectedProject.id, { testo: newNote.trim() });
      setNewNote('');
      fetchNotes(selectedProject.id);
      toast.success('Nota aggiunta');
    } catch {
      toast.error('Errore aggiunta nota');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await projectsAPI.deleteNote(selectedProject.id, noteId);
      fetchNotes(selectedProject.id);
      toast.success('Nota eliminata');
    } catch {
      toast.error('Errore eliminazione nota');
    }
  };

  const toggleService = (serviceId) => {
    const newServices = selectedServices.includes(serviceId)
      ? selectedServices.filter((s) => s !== serviceId)
      : [...selectedServices, serviceId];
    setValue('servizi', newServices);
  };

  const toggleTeamMember = (userId) => {
    const newTeam = selectedTeam.includes(userId)
      ? selectedTeam.filter((u) => u !== userId)
      : [...selectedTeam, userId];
    setValue('team_members', newTeam);
  };

  const getClientName = (clientId) => clients.find((c) => c.id === clientId)?.nome || 'N/A';
  const getUserName = (userId) => { const u = users.find((u) => u.id === userId); return u ? `${u.nome} ${u.cognome}` : 'N/A'; };
  const getServiceName = (serviceId) => services.find((s) => s.id === serviceId)?.nome || serviceId;

  const filteredProjects = projects
    .filter((project) => {
      const matchesSearch =
        project.nome.toLowerCase().includes(searchQuery.toLowerCase()) ||
        getClientName(project.client_id).toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || project.stato === statusFilter;
      const matchesMine = !myProjectsOnly || (project.team_members || []).includes(currentUser?.id);
      return matchesSearch && matchesStatus && matchesMine;
    })
    .sort((a, b) => {
      if (sortBy === 'az') return a.nome.localeCompare(b.nome, 'it');
      if (sortBy === 'za') return b.nome.localeCompare(a.nome, 'it');
      if (sortBy === 'budget_desc') return (b.budget || 0) - (a.budget || 0);
      if (sortBy === 'budget_asc') return (a.budget || 0) - (b.budget || 0);
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });

  const getProfitability = (project) => {
    if (!project.budget) return null;
    const profit = project.budget - (project.costo_totale || 0);
    const percentage = ((project.budget - project.costo_totale) / project.budget) * 100;
    return { profit, percentage };
  };

  const StatusActions = ({ project }) => (
    <>
      {project.stato === 'pianificato' && (
        <DropdownMenuItem onClick={() => handleStatusChange(project.id, 'in_corso')}>
          <Play className="h-4 w-4 mr-2" /> Avvia
        </DropdownMenuItem>
      )}
      {project.stato === 'in_corso' && (
        <>
          <DropdownMenuItem onClick={() => handleStatusChange(project.id, 'in_pausa')}>
            <Pause className="h-4 w-4 mr-2" /> Pausa
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleStatusChange(project.id, 'completato')}>
            <CheckCircle className="h-4 w-4 mr-2 text-emerald-600" /> Completa
          </DropdownMenuItem>
        </>
      )}
      {project.stato === 'in_pausa' && (
        <DropdownMenuItem onClick={() => handleStatusChange(project.id, 'in_corso')}>
          <Play className="h-4 w-4 mr-2" /> Riprendi
        </DropdownMenuItem>
      )}
      {project.stato === 'completato' && (
        <DropdownMenuItem onClick={() => handleStatusChange(project.id, 'in_corso')}>
          <Play className="h-4 w-4 mr-2" /> Riapri
        </DropdownMenuItem>
      )}
    </>
  );

  return (
    <Layout title="Progetti">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex gap-3 flex-1 flex-wrap">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Cerca progetti..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-projects-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="filter-project-status">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="pianificato">Pianificato</SelectItem>
                <SelectItem value="in_corso">In corso</SelectItem>
                <SelectItem value="in_pausa">In pausa</SelectItem>
                <SelectItem value="completato">Completato</SelectItem>
                <SelectItem value="annullato">Annullato</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Checkbox
                id="my-projects"
                checked={myProjectsOnly}
                onCheckedChange={setMyProjectsOnly}
              />
              <label htmlFor="my-projects" className="text-sm text-slate-600 cursor-pointer whitespace-nowrap">
                Solo i miei
              </label>
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Ordina" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recente">Più recente</SelectItem>
                <SelectItem value="az">A → Z</SelectItem>
                <SelectItem value="za">Z → A</SelectItem>
                <SelectItem value="budget_desc">Budget ↓</SelectItem>
                <SelectItem value="budget_asc">Budget ↑</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-0.5 bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm text-indigo-700' : 'text-slate-500 hover:text-slate-700'}`}
                title="Vista griglia"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-indigo-700' : 'text-slate-500 hover:text-slate-700'}`}
                title="Vista lista"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingProject(null);
              reset({ nome: '', client_id: '', servizi: [], team_members: [], budget: 0, descrizione_html: '' });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-project-btn">
                <Plus className="h-4 w-4 mr-2" /> Nuovo Progetto
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProject ? 'Modifica Progetto' : 'Nuovo Progetto'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome *</Label>
                    <Input {...register('nome')} data-testid="project-nome-input" />
                    {errors.nome && <p className="text-sm text-red-500">{errors.nome.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>Cliente *</Label>
                    <div className="flex gap-2">
                      <Controller
                        name="client_id"
                        control={control}
                        render={({ field }) => (
                          <Select onValueChange={field.onChange} value={field.value}>
                            <SelectTrigger data-testid="project-client-select" className="flex-1">
                              <SelectValue placeholder="Seleziona cliente" />
                            </SelectTrigger>
                            <SelectContent>
                              {clients.map((client) => (
                                <SelectItem key={client.id} value={client.id}>
                                  {client.nome}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <QuickClientDialog
                        onCreated={(c) => {
                          setClients((prev) => [...prev, c]);
                          setValue('client_id', c.id);
                        }}
                      />
                    </div>
                    {errors.client_id && <p className="text-sm text-red-500">{errors.client_id.message}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Budget (€)</Label>
                  <Input type="number" step="0.01" {...register('budget')} data-testid="project-budget-input" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data Inizio</Label>
                    <Controller
                      name="data_inizio"
                      control={control}
                      render={({ field }) => (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? format(field.value, 'dd/MM/yyyy', { locale: it }) : 'Seleziona data'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar mode="single" selected={field.value} onSelect={field.onChange} locale={it} />
                          </PopoverContent>
                        </Popover>
                      )}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Data Fine Prevista</Label>
                    <Controller
                      name="data_fine_prevista"
                      control={control}
                      render={({ field }) => (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? format(field.value, 'dd/MM/yyyy', { locale: it }) : 'Seleziona data'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar mode="single" selected={field.value} onSelect={field.onChange} locale={it} />
                          </PopoverContent>
                        </Popover>
                      )}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Servizi</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border rounded-lg">
                    {services.map((service) => (
                      <div key={service.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50">
                        <Checkbox
                          checked={selectedServices.includes(service.id)}
                          onCheckedChange={() => toggleService(service.id)}
                        />
                        <span className="text-sm">{service.nome}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Team</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border rounded-lg">
                    {users.map((user) => (
                      <div key={user.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50">
                        <Checkbox
                          checked={selectedTeam.includes(user.id)}
                          onCheckedChange={() => toggleTeamMember(user.id)}
                        />
                        <span className="text-sm">{user.nome} {user.cognome}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Descrizione</Label>
                  <Controller
                    name="descrizione_html"
                    control={control}
                    render={({ field }) => (
                      <RichTextEditor
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Descrizione del progetto..."
                        minHeight="120px"
                      />
                    )}
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-project-btn">
                    {editingProject ? 'Salva' : 'Crea'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Projects List View */}
        {viewMode === 'list' && (
          <Card className="table-wrapper" data-testid="projects-list">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="text-xs font-medium text-slate-400 uppercase tracking-wide">Progetto</TableHead>
                  <TableHead className="text-xs font-medium text-slate-400 uppercase tracking-wide">Stato</TableHead>
                  <TableHead className="hidden lg:table-cell text-xs font-medium text-slate-400 uppercase tracking-wide">Team</TableHead>
                  <TableHead className="hidden sm:table-cell text-xs font-medium text-slate-400 uppercase tracking-wide">Budget</TableHead>
                  <TableHead className="hidden lg:table-cell text-xs font-medium text-slate-400 uppercase tracking-wide">Ore</TableHead>
                  <TableHead className="hidden lg:table-cell text-xs font-medium text-slate-400 uppercase tracking-wide">Scadenza</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8"><div className="spinner mx-auto" /></TableCell>
                  </TableRow>
                ) : filteredProjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-400">Nessun progetto</TableCell>
                  </TableRow>
                ) : filteredProjects.map((project) => (
                  <TableRow key={project.id} className="table-row-hover group">
                    {/* Progetto + cliente + servizi */}
                    <TableCell className="py-3">
                      <div
                        className="font-bold text-gray-900 text-sm leading-snug cursor-pointer group-hover:text-indigo-700 transition-colors"
                        onClick={() => handleViewDetail(project)}
                      >
                        {project.nome}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">{getClientName(project.client_id)}</div>
                      {project.servizi?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {project.servizi.slice(0, 2).map(s => (
                            <Badge key={s} variant="outline" className="text-[10px] px-1.5 py-0 border-slate-200 text-slate-400 font-normal">
                              {getServiceName(s)}
                            </Badge>
                          ))}
                          {project.servizi.length > 2 && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-slate-200 text-slate-400 font-normal">
                              +{project.servizi.length - 2}
                            </Badge>
                          )}
                        </div>
                      )}
                    </TableCell>

                    {/* Stato */}
                    <TableCell className="py-3">
                      <Badge className={`${getStatusColor(project.stato)} text-xs`}>
                        {getStatusLabel(project.stato)}
                      </Badge>
                    </TableCell>

                    {/* Team avatars */}
                    <TableCell className="hidden lg:table-cell py-3">
                      {project.team_members?.length > 0 ? (
                        <div className="flex items-center -space-x-1.5">
                          {project.team_members.slice(0, 4).map((userId) => {
                            const u = users.find((u) => u.id === userId);
                            const initials = u ? `${u.nome?.[0] || ''}${u.cognome?.[0] || ''}`.toUpperCase() : '?';
                            return (
                              <div
                                key={userId}
                                title={u ? `${u.nome} ${u.cognome}` : 'Utente'}
                                className="w-6 h-6 rounded-full bg-indigo-100 border-2 border-white flex items-center justify-center cursor-default"
                              >
                                <span className="text-[9px] font-bold text-indigo-700">{initials}</span>
                              </div>
                            );
                          })}
                          {project.team_members.length > 4 && (
                            <div className="w-6 h-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center">
                              <span className="text-[9px] font-semibold text-slate-400">+{project.team_members.length - 4}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </TableCell>

                    {/* Budget */}
                    <TableCell className="hidden sm:table-cell py-3">
                      <span className="text-sm text-slate-500 tabular-nums">
                        {project.budget > 0 ? formatCurrency(project.budget) : <span className="text-slate-300">—</span>}
                      </span>
                    </TableCell>

                    {/* Ore */}
                    <TableCell className="hidden lg:table-cell py-3">
                      <span className="text-xs text-slate-400 tabular-nums">
                        {project.ore_totali > 0 ? `${project.ore_totali}h` : <span className="text-slate-300">—</span>}
                      </span>
                    </TableCell>

                    {/* Scadenza */}
                    <TableCell className="hidden lg:table-cell py-3">
                      {project.data_fine_prevista ? (
                        <span className={`flex items-center gap-1 text-xs ${
                          isOverdue(project.data_fine_prevista)
                            ? 'text-red-500 font-semibold'
                            : isWithinDays(project.data_fine_prevista, 7)
                            ? 'text-amber-500 font-medium'
                            : 'text-slate-400'
                        }`}>
                          {(isOverdue(project.data_fine_prevista) || isWithinDays(project.data_fine_prevista, 7)) && (
                            <AlertTriangle className="h-3 w-3 shrink-0" />
                          )}
                          {formatDate(project.data_fine_prevista)}
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </TableCell>

                    {/* Azioni */}
                    <TableCell className="py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`project-menu-list-${project.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewDetail(project)}>
                            <Eye className="h-4 w-4 mr-2" /> Dettagli
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(project)}>
                            <Pencil className="h-4 w-4 mr-2" /> Modifica
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <StatusActions project={project} />
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(project.id)}>
                            <Trash2 className="h-4 w-4 mr-2" /> Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        {/* Projects Grid */}
        {viewMode === 'grid' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
            {loading ? (
              [...Array(6)].map((_, i) => (
                <Card key={i} className="p-6">
                  <div className="animate-pulse space-y-4">
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                    <div className="h-3 bg-slate-200 rounded w-1/2" />
                    <div className="h-2 bg-slate-200 rounded w-full" />
                  </div>
                </Card>
              ))
            ) : filteredProjects.length === 0 ? (
              <div className="col-span-full text-center py-12 text-slate-400">Nessun progetto</div>
            ) : (
              filteredProjects.map((project) => {
                const profitability = getProfitability(project);
                return (
                  <Card key={project.id} className="card-hover">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-slate-900">{project.nome}</h3>
                          <p className="text-sm text-slate-500">{getClientName(project.client_id)}</p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`project-menu-${project.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewDetail(project)}>
                              <Eye className="h-4 w-4 mr-2" /> Dettagli
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEdit(project)}>
                              <Pencil className="h-4 w-4 mr-2" /> Modifica
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <StatusActions project={project} />
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(project.id)}>
                              <Trash2 className="h-4 w-4 mr-2" /> Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <Badge className={`mb-3 ${getStatusColor(project.stato)}`}>
                        {getStatusLabel(project.stato)}
                      </Badge>

                      <div className="space-y-3">
                        {project.budget > 0 && (
                          <div>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-slate-500">Budget</span>
                              <span className="font-medium currency">{formatCurrency(project.budget)}</span>
                            </div>
                            <Progress
                              value={Math.min(((project.costo_totale || 0) / project.budget) * 100, 100)}
                              className="h-1.5"
                            />
                            <div className="flex justify-between text-xs mt-1">
                              <span className="text-slate-400">Costo: {formatCurrency(project.costo_totale || 0)}</span>
                              {profitability && (
                                <span className={profitability.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                                  {profitability.profit >= 0 ? '+' : ''}{formatCurrency(profitability.profit)}
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <Clock className="h-3.5 w-3.5" /> {project.ore_totali || 0}h
                          </span>
                          {project.team_members?.length > 0 ? (
                            <div className="flex items-center -space-x-1.5">
                              {project.team_members.slice(0, 4).map((userId) => {
                                const u = users.find((u) => u.id === userId);
                                const initials = u
                                  ? `${u.nome?.[0] || ''}${u.cognome?.[0] || ''}`.toUpperCase()
                                  : '?';
                                return (
                                  <div
                                    key={userId}
                                    title={u ? `${u.nome} ${u.cognome}` : 'Utente'}
                                    className="w-6 h-6 rounded-full bg-indigo-100 border-2 border-white flex items-center justify-center cursor-default"
                                  >
                                    <span className="text-[9px] font-bold text-indigo-700">{initials}</span>
                                  </div>
                                );
                              })}
                              {project.team_members.length > 4 && (
                                <div className="w-6 h-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center">
                                  <span className="text-[9px] font-semibold text-slate-500">
                                    +{project.team_members.length - 4}
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Users className="h-3.5 w-3.5" /> —
                            </span>
                          )}
                        </div>

                        {project.servizi?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {project.servizi.slice(0, 3).map((s) => (
                              <Badge key={s} variant="outline" className="text-xs">
                                {getServiceName(s)}
                              </Badge>
                            ))}
                            {project.servizi.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{project.servizi.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={(open) => {
          setDetailDialogOpen(open);
          if (!open) { setTasks([]); setNewTaskTitle(''); }
        }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedProject?.nome}</DialogTitle>
            </DialogHeader>
            {selectedProject && (
              <Tabs defaultValue="overview">
                <TabsList>
                  <TabsTrigger value="overview">Panoramica</TabsTrigger>
                  <TabsTrigger value="tasks">
                    Attività {tasks.length > 0 && <span className="ml-1 text-xs bg-slate-200 text-slate-600 rounded-full px-1.5">{tasks.length}</span>}
                  </TabsTrigger>
                  <TabsTrigger value="time">Ore Lavorate</TabsTrigger>
                  <TabsTrigger value="attachments">
                    Allegati {attachments.length > 0 && <span className="ml-1 text-xs bg-slate-200 text-slate-600 rounded-full px-1.5">{attachments.length}</span>}
                  </TabsTrigger>
                  <TabsTrigger value="notes">
                    Note {notes.length > 0 && <span className="ml-1 text-xs bg-slate-200 text-slate-600 rounded-full px-1.5">{notes.length}</span>}
                  </TabsTrigger>
                </TabsList>

                {/* Panoramica */}
                <TabsContent value="overview" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="p-4">
                      <p className="text-xs text-slate-500">Budget</p>
                      <p className="text-lg font-bold currency">{formatCurrency(selectedProject.budget)}</p>
                    </Card>
                    <Card className="p-4">
                      <p className="text-xs text-slate-500">Costo</p>
                      <p className="text-lg font-bold currency">{formatCurrency(selectedProject.costo_totale)}</p>
                    </Card>
                    <Card className="p-4">
                      <p className="text-xs text-slate-500">Ore</p>
                      <p className="text-lg font-bold">{selectedProject.ore_totali || 0}h</p>
                    </Card>
                    <Card className="p-4">
                      <p className="text-xs text-slate-500">Margine</p>
                      <p className={`text-lg font-bold ${(selectedProject.budget - selectedProject.costo_totale) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(selectedProject.budget - (selectedProject.costo_totale || 0))}
                      </p>
                    </Card>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-500">Cliente</Label>
                      <p className="font-medium">{getClientName(selectedProject.client_id)}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Stato</Label>
                      <Badge className={getStatusColor(selectedProject.stato)}>
                        {getStatusLabel(selectedProject.stato)}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-slate-500">Data Inizio</Label>
                      <p>{formatDate(selectedProject.data_inizio)}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Data Fine Prevista</Label>
                      <p>{formatDate(selectedProject.data_fine_prevista)}</p>
                    </div>
                  </div>

                  {selectedProject.team_members?.length > 0 && (
                    <div>
                      <Label className="text-slate-500">Team</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {selectedProject.team_members.map((userId) => (
                          <Badge key={userId} variant="outline">{getUserName(userId)}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedProject.descrizione_html ? (
                    <div>
                      <Label className="text-slate-500">Descrizione</Label>
                      <div
                        className="text-sm text-slate-700 mt-1 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                        dangerouslySetInnerHTML={{ __html: selectedProject.descrizione_html }}
                      />
                    </div>
                  ) : selectedProject.descrizione ? (
                    <div>
                      <Label className="text-slate-500">Descrizione</Label>
                      <p className="text-sm text-slate-600 mt-1">{selectedProject.descrizione}</p>
                    </div>
                  ) : null}
                </TabsContent>

                {/* Attività / Sotto-attività */}
                <TabsContent value="tasks" className="space-y-4 mt-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Aggiungi un'attività..."
                      value={newTaskTitle}
                      onChange={(e) => setNewTaskTitle(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAddTask(); }}
                      className="flex-1"
                    />
                    <Button
                      className="bg-indigo-600 hover:bg-indigo-700"
                      onClick={handleAddTask}
                      disabled={!newTaskTitle.trim()}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {tasks.length === 0 ? (
                    <div className="text-center py-10 text-slate-400">
                      <ListTodo className="h-8 w-8 mx-auto mb-2 opacity-40" />
                      <p>Nessuna attività</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {tasks.map((task, idx) => (
                        <div
                          key={task.id}
                          className={`border rounded-lg transition-colors ${task.completata ? 'bg-slate-50 border-slate-100' : 'bg-white hover:bg-slate-50'}`}
                        >
                          <div className="flex items-center gap-2 p-3">
                            <Checkbox
                              checked={!!task.completata}
                              onCheckedChange={() => handleToggleTask(task)}
                            />
                            {task.priorita && task.priorita !== 'none' && (
                              <span className={`w-2 h-2 rounded-full shrink-0 ${task.priorita === 'alta' ? 'bg-red-400' : task.priorita === 'media' ? 'bg-amber-400' : 'bg-slate-400'}`} title={`Priorità ${task.priorita}`} />
                            )}
                            <div className="flex-1 min-w-0">
                              <span className={`text-sm ${task.completata ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                                {task.titolo}
                              </span>
                              {task.descrizione && (
                                <div
                                  className="text-xs text-slate-500 mt-0.5 prose prose-xs max-w-none [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 line-clamp-2"
                                  dangerouslySetInnerHTML={{ __html: task.descrizione }}
                                />
                              )}
                              {task.completata && task.completata_da && (
                                <p className="text-xs text-slate-400 mt-0.5">
                                  Completata da {getUserName(task.completata_da)}
                                  {task.completata_at && ` · ${formatDate(task.completata_at)}`}
                                </p>
                              )}
                            </div>
                            {task.assegnato_a && (
                              <span className="text-xs text-slate-400 hidden sm:block shrink-0">{getUserName(task.assegnato_a)}</span>
                            )}
                            {task.data_scadenza && (
                              <span className={`text-xs hidden sm:block shrink-0 ${isOverdue(task.data_scadenza) && !task.completata ? 'text-red-500 font-medium' : 'text-slate-400'}`}>
                                {formatDate(task.data_scadenza)}
                              </span>
                            )}
                            <div className="flex gap-0.5 shrink-0">
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400" disabled={idx === 0} onClick={() => handleMoveTask(task, 'up', idx)}>
                                <ChevronUp className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400" disabled={idx === tasks.length - 1} onClick={() => handleMoveTask(task, 'down', idx)}>
                                <ChevronDown className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500" onClick={() => handleOpenEditTask(task)}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="text-red-500 h-7 w-7" onClick={() => handleDeleteTask(task.id)}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="text-xs text-slate-400 text-right pt-1">
                        {tasks.filter(t => t.completata).length}/{tasks.length} completate
                      </div>
                    </div>
                  )}

                  {/* Task Edit Dialog — annidato dentro il Detail Dialog per evitare conflitti z-index */}
                  <Dialog open={taskEditOpen} onOpenChange={(open) => { if (!open) { setTaskEditOpen(false); setEditingTask(null); } }}>
                    <DialogContent className="max-w-md" aria-describedby={undefined}>
                      <DialogHeader>
                        <DialogTitle>Modifica Attività</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Titolo *</Label>
                          <Input
                            value={taskEditForm.titolo}
                            onChange={(e) => setTaskEditForm(f => ({ ...f, titolo: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Descrizione</Label>
                          <RichTextEditor
                            key={editingTask?.id}
                            value={taskEditForm.descrizione}
                            onChange={(v) => setTaskEditForm(f => ({ ...f, descrizione: v }))}
                            placeholder="Dettagli dell'attività..."
                            minHeight="100px"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Assegnata a</Label>
                          <Select
                            value={taskEditForm.assegnato_a || 'none'}
                            onValueChange={(v) => setTaskEditForm(f => ({ ...f, assegnato_a: v }))}
                          >
                            <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Nessuno</SelectItem>
                              {users.map(u => (
                                <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Data Scadenza</Label>
                          <Input
                            type="date"
                            value={taskEditForm.data_scadenza}
                            onChange={(e) => setTaskEditForm(f => ({ ...f, data_scadenza: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Priorità</Label>
                          <Select
                            value={taskEditForm.priorita || 'none'}
                            onValueChange={(v) => setTaskEditForm(f => ({ ...f, priorita: v }))}
                          >
                            <SelectTrigger><SelectValue placeholder="Nessuna" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Nessuna</SelectItem>
                              <SelectItem value="bassa">Bassa</SelectItem>
                              <SelectItem value="media">Media</SelectItem>
                              <SelectItem value="alta">Alta</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                          <Button variant="outline" onClick={() => setTaskEditOpen(false)}>Annulla</Button>
                          <Button
                            className="bg-indigo-600 hover:bg-indigo-700"
                            onClick={handleSaveEditTask}
                            disabled={!taskEditForm.titolo.trim()}
                          >
                            Salva
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </TabsContent>

                {/* Ore Lavorate */}
                <TabsContent value="time" className="space-y-4 mt-4">
                  <div className="flex justify-end">
                    <Button
                      className="bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => {
                        setEditingTimeEntry(null);
                        resetTime({ user_id: '', ore: 1, data: new Date().toISOString().split('T')[0], costo_orario: 50 });
                        setTimeDialogOpen(true);
                      }}
                      data-testid="add-time-entry-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" /> Registra Ore
                    </Button>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Utente</TableHead>
                        <TableHead>Ore</TableHead>
                        <TableHead>Costo</TableHead>
                        <TableHead>Descrizione</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {timeEntries.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-slate-400">Nessuna registrazione</TableCell>
                        </TableRow>
                      ) : (
                        timeEntries.map((entry) => (
                          <TableRow key={entry.id}>
                            <TableCell>{formatDate(entry.data)}</TableCell>
                            <TableCell>{getUserName(entry.user_id)}</TableCell>
                            <TableCell>{entry.ore}h</TableCell>
                            <TableCell className="currency">{formatCurrency(entry.ore * (entry.costo_orario || 0))}</TableCell>
                            <TableCell className="text-slate-500">{entry.descrizione || '-'}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditTimeEntry(entry)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-600"
                                  onClick={() => handleDeleteTimeEntry(entry.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TabsContent>

                {/* Allegati */}
                <TabsContent value="attachments" className="space-y-4 mt-4">
                  <div className="flex justify-end">
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                    <Button
                      className="bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingFile}
                    >
                      <Paperclip className="h-4 w-4 mr-2" />
                      {uploadingFile ? 'Caricamento...' : 'Carica Allegato'}
                    </Button>
                  </div>
                  {attachments.length === 0 ? (
                    <div className="text-center py-10 text-slate-400">
                      <Paperclip className="h-8 w-8 mx-auto mb-2 opacity-40" />
                      <p>Nessun allegato</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {attachments.map((attach) => (
                        <div key={attach.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                          <div className="flex items-center gap-3 min-w-0">
                            <FileText className="h-5 w-5 text-slate-400 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{attach.nome}</p>
                              <p className="text-xs text-slate-400">
                                {attach.file_type && <span className="mr-2">{attach.file_type}</span>}
                                {formatFileSize(attach.file_size)}
                                {attach.created_at && <span className="ml-2">{formatDate(attach.created_at)}</span>}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button variant="ghost" size="icon" onClick={() => handleDownloadAttachment(attach)} title="Scarica">
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-red-600" onClick={() => handleDeleteAttachment(attach.id)} title="Elimina">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Note */}
                <TabsContent value="notes" className="space-y-4 mt-4">
                  <div className="flex gap-2">
                    <Textarea
                      placeholder="Aggiungi una nota..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows={2}
                      className="flex-1"
                      onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) handleAddNote(); }}
                    />
                    <Button
                      className="bg-indigo-600 hover:bg-indigo-700 self-end"
                      onClick={handleAddNote}
                      disabled={!newNote.trim()}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                  {notes.length === 0 ? (
                    <div className="text-center py-10 text-slate-400">
                      <StickyNote className="h-8 w-8 mx-auto mb-2 opacity-40" />
                      <p>Nessuna nota</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {notes.map((note) => (
                        <div key={note.id} className="flex gap-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                          <StickyNote className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.testo}</p>
                            <p className="text-xs text-slate-400 mt-1">{formatDate(note.created_at)}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 shrink-0 h-7 w-7"
                            onClick={() => handleDeleteNote(note.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>

        {/* Time Entry Dialog */}
        <Dialog open={timeDialogOpen} onOpenChange={(open) => { setTimeDialogOpen(open); if (!open) setEditingTimeEntry(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingTimeEntry ? 'Modifica Ore' : 'Registra Ore'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitTime(onSubmitTime)} className="space-y-4">
              <div className="space-y-2">
                <Label>Membro del team *</Label>
                <Controller
                  name="user_id"
                  control={controlTime}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger data-testid="time-user-select">
                        <SelectValue placeholder="Seleziona persona" />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          const teamIds = selectedProject?.team_members ?? [];
                          const teamUsers = users.filter(u => teamIds.includes(u.id));
                          const otherUsers = users.filter(u => !teamIds.includes(u.id));
                          return (
                            <>
                              {teamUsers.length > 0 && (
                                <>
                                  <div className="px-2 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                                    Team del progetto
                                  </div>
                                  {teamUsers.map(u => (
                                    <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                                  ))}
                                </>
                              )}
                              {otherUsers.length > 0 && (
                                <>
                                  {teamUsers.length > 0 && (
                                    <div className="px-2 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wider border-t mt-1 pt-2">
                                      Altri utenti
                                    </div>
                                  )}
                                  {otherUsers.map(u => (
                                    <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                                  ))}
                                </>
                              )}
                            </>
                          );
                        })()}
                      </SelectContent>
                    </Select>
                  )}
                />
                {timeErrors.user_id && <p className="text-sm text-red-500">{timeErrors.user_id.message}</p>}
              </div>

              <div className="space-y-2">
                <Label>Data *</Label>
                <Input type="date" {...registerTime('data')} />
                {timeErrors.data && <p className="text-sm text-red-500">{timeErrors.data.message}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ore *</Label>
                  <Input type="number" step="0.5" {...registerTime('ore')} data-testid="time-ore-input" />
                  {timeErrors.ore && <p className="text-sm text-red-500">{timeErrors.ore.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label>Costo Orario (€)</Label>
                  <Input type="number" step="0.01" {...registerTime('costo_orario')} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Descrizione</Label>
                <Textarea {...registerTime('descrizione')} rows={2} />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setTimeDialogOpen(false); setEditingTimeEntry(null); }}>Annulla</Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-time-btn">Salva</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
